import type { IOptionItem, ISuccessApiResponse } from '@wyz/types';
import { PublicTypeAheadFieldProps, TypeAheadFetcher } from '@wyz/ui';
import { invariant } from '@wyz/utils';
import { useFormContext } from 'react-hook-form';
import { TypeaheadField } from '~/common/components';
import { Trans, useApi, useConfig, useI18n } from '~/common/context';
import { usePurchaseContext } from '~/purchase/context';
import { IWcwSearchFormSchema } from '~/purchase/schemas';

type WcwAutocompleteItem = IOptionItem;

type Props = Pick<
  PublicTypeAheadFieldProps<false, WcwAutocompleteItem>,
  'size'
> & {
  disabled?: boolean;
};

function WcwAutocomplete({ disabled, ...props }: Props) {
  const { t } = useI18n();
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const { state } = usePurchaseContext();
  const methods = useFormContext<IWcwSearchFormSchema>();

  const idCustomer = state.targetCustomer?.id;
  invariant(idCustomer);

  const referenceFetcher: TypeAheadFetcher<WcwAutocompleteItem> = async (
    keyword,
    { signal },
  ) => {
    const data = await callCoreApi<
      ISuccessApiResponse<Array<{ id: string; reference: string }>>
    >(
      `/tenants/api/wtb/tenants/${tenantCode}/customers/${idCustomer}/wheels/autocomplete/reference?q=${encodeURIComponent(keyword)}`,
      { signal },
    );
    return data.data.map(({ reference }) => ({
      label: reference,
      value: reference,
    }));
  };

  const nameFetcher: TypeAheadFetcher<WcwAutocompleteItem> = async (
    keyword,
    { signal },
  ) => {
    const data = await callCoreApi<
      ISuccessApiResponse<
        Array<{ id: string; reference: string; name?: string }>
      >
    >(
      `/tenants/api/wtb/tenants/${tenantCode}/customers/${idCustomer}/wheels/autocomplete/name?q=${encodeURIComponent(keyword)}`,
      { signal },
    );
    return data.data.map(({ reference, name }) => ({
      label: name ?? reference,
      value: reference,
    }));
  };

  const handleInputChange = (inputValue: string) => {
    methods.setValue('rawIdentifier', inputValue);
  };

  return (
    <TypeaheadField<false, WcwAutocompleteItem>
      name='identifier'
      label={<Trans i18nKey='app.purchase.search_page.quick_search' />}
      placeholder={
        <Trans i18nKey='app.purchase.wcw.search_page.quick_search_placeholder' />
      }
      fetcher={[referenceFetcher, nameFetcher]}
      isDisabled={disabled}
      minCharacter={3}
      isClearable
      size='sm'
      onSelect={(item) => {
        if (!item) {
          methods.setValue('rawIdentifier', null);
          methods.setValue('identifier', null);
        }
      }}
      onInputChange={handleInputChange}
      mapper={(groups) => [
        {
          label: t('app.purchase.wcw.search_page.autocomplete.reference'),
          options: groups[0] || [],
        },
        {
          label: t('app.purchase.wcw.search_page.autocomplete.name'),
          options: groups[1] || [],
        },
      ]}
      showCount={false}
      {...props}
    />
  );
}

export default WcwAutocomplete;
